import classNames from "classnames/dedupe";
import React, { FC } from "react";
import useWatchState from "../../../../hooks/useWatchState";
import { TileCheckbox } from "../../../ActionsSelectors/Checkbox";
import { ScrollContainer } from "../../../Utility/ScrollContainer";
import { useTheme } from "../../../Utility/ThemeProvider";
import { AbstractFilter } from "../AbstractFilter";
import { ICheckboxFilterItem } from "../Filter.interface";
import FilterFormItemWrapper from "../FilterFormItemWrapper/FilterFormItemWrapper";
import { ITileFilterProps } from "./TileFilter.interface";
import { tileToFilter } from "./tileToFilter";

const TileFilter: FC<ITileFilterProps> = ({
  filterData,
  singleValue,
  labels,
  variant,
  asPureFilter,
  onChange,
  ...abstractFilterProps
}) => {
  const [filterItemsState, setFilterItems] = useWatchState<
    ICheckboxFilterItem[]
  >({ value: filterData.items });

  const handleOnSelect = ({
    id,
    newChecked,
  }: {
    id: string;
    newChecked: boolean;
  }) => {
    const setCheckedState = (item: ICheckboxFilterItem) => {
      if (item.id === id) return newChecked;
      return singleValue ? false : item.checked;
    };

    const updatedItems = filterItemsState.map((item) => ({
      ...item,
      checked: setCheckedState(item),
    }));

    setFilterItems(updatedItems);
    onChange &&
      onChange({
        values: updatedItems,
        updates: singleValue
          ? [tileToFilter(filterItemsState[0])]
          : filterItemsState.filter((item) => item.id === id).map(tileToFilter),
      });
  };

  const clearCallback = () => {
    const updatedItems = filterItemsState.map((item) => ({
      ...item,
      checked: false,
    }));
    setFilterItems(updatedItems);
    onChange &&
      onChange({
        values: updatedItems,
        updates: updatedItems.map(tileToFilter),
      });
  };

  const checkedItems = filterItemsState.filter((item) => item.checked === true);

  const selectedItems = checkedItems
    .map((item) => item.title || item.color)
    .join(", ");

  const totalItemsCount = checkedItems.length;

  const theme = useTheme(variant);

  const tileClasses = classNames("filter-form-tile", {
    "filter-form-tile--light": theme === "light",
  });

  const tileBodyClasses = classNames("filter-form-tile__body", {
    "filter-form-tile__body--light": theme === "light",
  });

  const filterBody = (
    <div className={tileClasses}>
      <div className={tileBodyClasses}>
        {filterItemsState.map(({ itemsCount, ...checkbox }) => {
          return (
            <FilterFormItemWrapper
              key={`${checkbox.id}-${itemsCount}`}
              type="tile"
            >
              <TileCheckbox
                {...checkbox}
                count={itemsCount}
                name={filterData?.name}
                onChange={(e) =>
                  handleOnSelect({
                    id: checkbox.id,
                    newChecked: !!e.target.checked,
                  })
                }
              />
            </FilterFormItemWrapper>
          );
        })}
      </div>
    </div>
  );

  if (asPureFilter) {
    return filterBody;
  }

  return (
    <AbstractFilter
      variant={variant}
      title={filterData?.categoryName}
      subtitle={selectedItems}
      badge={{
        count: totalItemsCount,
      }}
      clearAll={{
        clearAllText: labels?.clearAll,
        clearAllCallback: clearCallback,
      }}
      {...abstractFilterProps}
    >
      {abstractFilterProps.filterType === "accordion" ? (
        filterBody
      ) : (
        <ScrollContainer>{filterBody}</ScrollContainer>
      )}
    </AbstractFilter>
  );
};

export default TileFilter;
